// Styles
import '../../MapStyles/MapMenu.scss';

// Utils
import { lowercase } from '@utils';

// React & Hooks
import { useEffect, useState } from 'react';

// React Router
import { useHistory } from 'react-router-dom';

// React Bootstrap
import { Button, Card, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';

// Components
import { Icon, Text, Keywords } from '@components';
import { MapColorPicker } from '../MapMiscComponents/MapColorPicker';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';
import moment from 'moment';

// Helper function to format the 'selected' value
const formatSelectedText = (selected: string) => {
  if (!selected) return 'Item';
  if (selected === 'stationary') return 'Stationary Asset';
  return selected.charAt(0).toUpperCase() + selected.slice(1);
};

export function MapEditItem() {
  const clickEvent = useMapStore((state) => state.clickEvent);
  const selected = useMapStore((state) => state.selected);
  const mapLayers = useMapStore((state) => state.mapLayers);
  const movingItem = useMapStore((state) => state.movingItem);
  const deleteObject = useMapStore((state) => state.deleteObject);
  const updateObject = useMapStore((state) => state.updateObject);
  const setShowMore = useMapStore((state) => state.setShowMore);
  const setIsDirty = useMapStore((state) => state.setIsDirty);
  const setMapUpdated = useMapStore((state) => state.setMapUpdated);
  const setMovingItem = useMapStore((state) => state.setMovingItem);
  const setEditItem = useMapStore((state) => state.setEditItem);
  const setIsEditingItem = useMapStore((state) => state.setIsEditingItem);

  const [invalid, setInvalid] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemColor, setItemColor] = useState(
    clickEvent.feature.properties.BoundaryColor
      ? clickEvent.feature.properties.BoundaryColor
      : '#0048ff',
  );
  const history = useHistory();

  const isAllowedKey = (key: string): boolean => {
    return ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(key);
  };

  const setName = (value) => {
    clickEvent.feature.properties.Name = value;
  };

  const setItemKeywords = (value) => {
    clickEvent.feature.properties.Keywords = value;
  };

  const isDigit = (key: string): boolean => /^\d$/.test(key);

  const tooltip = (
    <Tooltip className="custom-tooltip">
      <MapColorPicker
        setObjColor={(color) => {
          setItemColor(color);
          clickEvent.feature.properties.BoundaryColor = color;
          clickEvent.setStyle({ color: color });
        }}
      />
    </Tooltip>
  );

  return (
    <Card>
      {clickEvent ? (
        <div className="object-wrapper-expand">
          <div>
            {clickEvent.feature.properties.EntityType !== 'Asset' && (
              <div className="status-row d-flex flex-row align-items-center justify-content-between back-button">
                {(selected === 'asset' || selected === 'stationary') && (
                  <Button
                    title="Go to Asset"
                    className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                    variant="light"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      history.push('/assets/' + clickEvent.feature.properties.Id);
                      e.stopPropagation();
                    }}
                  >
                    <i className={`sonarIcon sonarIcon-open-in fs-md`} />
                  </Button>
                )}
                {selected === 'place' && (
                  <Button
                    title="Go to Place"
                    className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                    variant="light"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      history.push('/places/' + clickEvent.feature.properties.Id);
                      e.stopPropagation();
                    }}
                  >
                    <i className={`sonarIcon sonarIcon-open-in fs-md`} />
                  </Button>
                )}
                {selected === 'place' && (
                  <OverlayTrigger trigger="focus" placement="bottom" overlay={tooltip}>
                    <Button
                      title="Edit Color"
                      className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                      variant="light"
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                    >
                      <i
                        style={{
                          color: itemColor,
                        }}
                        className={`sonarIcon sonarIcon-color fs-md`}
                      />
                    </Button>
                  </OverlayTrigger>
                )}
                {!movingItem ? (
                  <Icon
                    name={'pin-edit'}
                    title="Edit Placement"
                    className="align-sub primary-icon"
                    variant="light"
                    disabled={
                      !mapLayers.includes(
                        lowercase(clickEvent.feature.properties.EntityType) + 's',
                      ) &&
                      selected !== lowercase(clickEvent.feature.properties.EntityType).split(' ')[0]
                    }
                    size="md"
                    onClick={() => {
                      setMovingItem(true);
                      setEditItem(clickEvent);
                      setIsEditingItem(true);
                    }}
                  />
                ) : (
                  <Icon
                    name={'check'}
                    title="Confirm"
                    className="align-sub primary-icon"
                    variant="light"
                    disabled={invalid}
                    size="md"
                    onClick={() => {
                      setMapUpdated(true);
                      setMovingItem(false);
                      updateObject(clickEvent);
                      setEditItem(null);
                      setShowMore(null);
                      setIsEditingItem(false);
                    }}
                  />
                )}
                <Icon
                  name={'trash'}
                  title={`Delete ${formatSelectedText(selected)}`}
                  className="align-sub danger-icon"
                  variant="light"
                  disabled={false}
                  size="md"
                  onClick={() => {
                    setEditItem(null);
                    deleteObject();
                    setMovingItem(false);
                    setIsDirty(true);
                    setShowMore(null);
                  }}
                />
                <Button
                  title={selected ? 'Back' : 'Close'}
                  className="btn-light d-flex flex-row align-items-center justify-content-between nav-text-icon"
                  variant="light"
                  onClick={() => {
                    setEditItem(null);
                    setShowMore(null);
                    setMovingItem(false);
                  }}
                >
                  {selected ? (
                    <i className="sonarIcon sonarIcon-left-arrow fs-md" />
                  ) : (
                    <i className="sonarIcon sonarIcon-add sonarIcon-rotate-45 fs-md nav-text-icon" />
                  )}
                </Button>
              </div>
            )}
          </div>
          {clickEvent.feature && (
            <div className="edit-feature">
              {clickEvent.feature.properties.Name && (
                <>
                  {clickEvent.feature.properties.EntityType === 'Place' ? (
                    <InputGroup className="edit-field">
                      <Text className="fw-semibold fs-md d-flex align-items-center">
                        Place: &nbsp;
                      </Text>
                      <Form.Control
                        key={clickEvent.feature.properties.Id}
                        defaultValue={clickEvent.feature.properties.Name}
                        aria-label="Name"
                        isInvalid={invalid}
                        onChange={(e) => {
                          if (e.target.value) {
                            setInvalid(false);
                            setName(e.target.value);
                          } else {
                            setInvalid(true);
                          }
                        }}
                      />
                    </InputGroup>
                  ) : (
                    <div className="status-row d-flex align-items-center">
                      <Text className="fw-semibold fs-md">Name: &nbsp;</Text>
                      <Text className="fs-md">{clickEvent.feature.properties.Name}</Text>
                    </div>
                  )}
                </>
              )}
              {clickEvent.feature.properties.DeviceId && (
                <div className="status-row d-flex align-items-center">
                  <Text className="fw-semibold fs-md">Device ID: &nbsp;</Text>
                  <Text className="fs-md">{clickEvent.feature.properties.DeviceId}</Text>
                </div>
              )}
              {clickEvent.feature.properties.LastCheckIn && (
                <div className="status-row d-flex align-items-center">
                  <Text className="fw-semibold fs-md">Last Check-In: &nbsp;</Text>
                  <Text className="fs-md">
                    {moment.utc(clickEvent.feature.properties.LastCheckIn).local().format('lll')}
                  </Text>
                </div>
              )}
              {clickEvent.feature.properties.OnlineStatus && (
                <div className="status-row d-flex align-items-center">
                  <Text className="fw-semibold fs-md">Status: &nbsp;</Text>
                  <Text className="fs-md">
                    {clickEvent.feature.properties.OnlineStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}
                  </Text>
                </div>
              )}
              {clickEvent.feature.properties.Floor && (
                <>
                  {clickEvent.feature.properties.EntityType === 'Gateway' ||
                  clickEvent.feature.properties.EntityType === 'Stationary Asset' ? (
                    <InputGroup className="edit-field">
                      <Text className="fw-semibold fs-md d-flex align-items-center">
                        Floor: &nbsp;
                      </Text>
                      <Form.Control
                        key={clickEvent.feature.properties.Id}
                        defaultValue={clickEvent.feature.properties.Floor}
                        aria-label="Floor"
                        type="text"
                        inputMode="numeric"
                        isInvalid={invalid}
                        onKeyDown={(e) => {
                          const { key, currentTarget } = e;
                          const { value, selectionStart } = currentTarget;

                          if (isAllowedKey(key)) return;

                          // Only allow digits
                          if (!isDigit(key)) {
                            e.preventDefault();
                            return;
                          }

                          // Allow "0" only if it's the *only* character
                          if (value === '' && key === '0') return;

                          // Block if trying to make a number like "01", "012", etc
                          if (value === '0') {
                            e.preventDefault();
                            return;
                          }

                          // Prevent leading 0 if already typing
                          if (value.length === 1 && value.startsWith('0')) {
                            e.preventDefault();
                            return;
                          }
                        }}
                        onChange={(e) => {
                          if (parseInt(e.target.value) >= 1) {
                            setInvalid(false);
                            clickEvent.feature.properties.Floor = e.target.value;
                          } else {
                            setInvalid(true);
                          }
                        }}
                      />
                    </InputGroup>
                  ) : (
                    <div className="status-row d-flex align-items-center">
                      <Text className="fw-semibold fs-md">Floor: &nbsp;</Text>
                      <Text className="fs-md">{clickEvent.feature.properties.Floor}</Text>
                    </div>
                  )}
                </>
              )}
              <br />
              {clickEvent.feature.properties.EntityType === 'Place' && (
                <div className="d-flex flex-column flex-wrap align-items-start">
                  <Text className="fw-bold fs-md me-2 mb-2">Keywords:</Text>
                  <Keywords
                    initKeywords={clickEvent.feature.properties.Keywords}
                    isAddingKeyword
                    onChange={(keywords) => setItemKeywords(keywords)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="object-wrapper-expand p-3 d-flex justify-content-center">
          <Text className="fw-semibold fs-md d-flex align-items-center text-center">
            This {formatSelectedText(selected)} has been deleted. <br /> Click the Save Button Above
            to save this change.
          </Text>
        </div>
      )}
    </Card>
  );
}
